import React from "react";

/**
 * Iter-258 §5: Details grid on the ad-detail page. Renders a compact
 * two-column list of key facts drawn from top-level listing fields and
 * `listing.attributes`.
 *
 * Iter-267 §1 defensive guard: Make/Model/Year/Condition and the
 * background-check tri-state chips are ONLY relevant to vehicle
 * families (cars, bikes, boats, caravans, trucks, marine, commercial
 * vehicles). Even if a legacy or badly-cleaned backend payload leaks
 * `listing.make`/`listing.model`/`listing.year` on a non-vehicle
 * listing (e.g. a Property or Pets ad), the primary row MUST NOT
 * render them. The backend now strips these at create/edit/read time,
 * but this guard is belt-and-braces.
 *
 * Hide-list (per bulletin §5 + §12):
 *   - `ad_address` — the address line already sits under the price card.
 *   - `Secondary Contact *` — the backend already strips these from
 *     public payloads; defensive filter here in case of legacy rows.
 *   - `Features` — rendered as a chip block further down (§7).
 *   - Rating trios (`* Rating|Average|Description`) — rendered in the
 *     Ratings block (§9).
 *   - Background-check toggles (Stolen / Damaged Import / Re-registered
 *     / Money Owing) — rendered as PASSED/FAILED chips (§6).
 *   - Plate — surfaced through the CarJam CTA (§13).
 *   - Top-level dupes (Make/Model/Year/Condition) — rendered inline
 *     in the primary row.
 */
const RATING_KEY_RE = /\b(Rating|Average|Description)$/;
const BG_CHECK_KEYS = new Set([
  "Stolen Vehicle",
  "Damaged Import Check",
  "Re-registered Check",
  "Money Owing",
]);
const VEHICLE_FAMILY_SLUGS = new Set([
  "cars",
  "bikes",
  "boats",
  "caravans",
  "trucks",
  "marine",
  "commercial-vehicles",
  "motorbikes",
]);
const isVehicleFamily = (listing) => {
  const p = listing?.parent_category_slug;
  const c = listing?.category_slug;
  return VEHICLE_FAMILY_SLUGS.has(p) || VEHICLE_FAMILY_SLUGS.has(c);
};
const HIDE_EXACT = new Set([
  "ad_address",
  "Address",
  "Features",
  "Plate",
  "plate",
  "Make",
  "Model",
  "Model Detail",
  "Year",
  "End Year",
  "Condition",
]);

const prettify = (s) => {
  if (!s) return s;
  // Snake-case → Title Case for legacy keys ("ad_address" style).
  if (/[_-]/.test(s)) {
    return String(s)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return s;
};

const renderValue = (v) => {
  if (v == null || v === "") return null;
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return null;
  return String(v);
};

export default function DetailsRow({ listing }) {
  const a = listing.attributes || {};
  const vehicle = isVehicleFamily(listing);
  // Primary row — always shown first so the eye finds Condition/Make/Model/
  // Year at the top of the Details block. Make/Model/Year are gated on
  // the vehicle-family guard; Condition can appear on any listing.
  const primary = [
    [
      "Condition",
      listing.condition
        ? String(listing.condition).charAt(0).toUpperCase() +
          String(listing.condition).slice(1)
        : a.Condition || a.condition || (listing.condition_new ? "New" : null),
    ],
    ["Make", vehicle ? (listing.make || a.Make) : null],
    ["Model", vehicle ? (listing.model || a.Model) : null],
    ["Year", vehicle ? (listing.year || a.Year) : null],
  ].filter(([, v]) => v);

  // Secondary grid — any remaining attribute keys not hidden above.
  // Vehicle-only keys are also stripped on non-vehicle listings.
  const VEHICLE_ONLY_ATTR_KEYS = new Set([
    "Make",
    "Model",
    "Model Detail",
    "Year",
    "End Year",
    "Odometer",
    "Transmission",
    "Body Style",
    "Fuel Type",
    "Engine Size",
    "Doors",
    "Seats",
    "Drive Type",
    "Kilometres",
    "Mileage",
    "Registration",
    "WOF Expiry",
    "Rego Expiry",
  ]);
  const grid = Object.entries(a)
    .filter(([k, v]) => {
      if (v == null || v === "") return false;
      if (HIDE_EXACT.has(k)) return false;
      if (k.startsWith("Secondary Contact")) return false;
      if (k.startsWith("Agency ")) return false;
      if (BG_CHECK_KEYS.has(k)) return false;
      if (RATING_KEY_RE.test(k)) return false;
      if (!vehicle && VEHICLE_ONLY_ATTR_KEYS.has(k)) return false;
      return renderValue(v) != null;
    })
    .map(([k, v]) => [prettify(k), renderValue(v)]);

  if (!primary.length && !grid.length) return null;

  // Iter-382 — legacy-style highlight pills: rounded white chip, soft
  // border + subtle shadow, label in dark text, value in brand orange.
  const pill = ([k, v]) => (
    <span
      key={k}
      className="inline-flex items-baseline gap-1.5 rounded-full bg-white border border-slate-200 shadow-sm px-3 py-1.5 text-sm max-w-full"
      data-testid={`ad-details-row-${k
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")}`}
    >
      <span className="font-semibold text-slate-700 whitespace-nowrap">{k}</span>
      <span className="font-bold text-[color:var(--brand-orange)] break-words">{v}</span>
    </span>
  );

  return (
    <div data-testid="ad-details-row" className="mb-6 pb-6 border-b border-subtle">
      <h2 className="font-display text-lg font-bold text-slate-800 mb-3">
        Details
      </h2>
      <div className="flex flex-wrap gap-2" data-testid="ad-details-pills">
        {primary.map(pill)}
        {grid.map(pill)}
      </div>
    </div>
  );
}
